import React, { useEffect, useState } from 'react';
import { TagIcon } from '@heroicons/react/24/outline';
import inventoryApi from '../../../services/inventoryApi';
import { downloadXlsxFromAoa } from '../../../utils/excelJsSheetHelpers';
import {
  ReportShell, Field, DateInput, ProductFilters, EMPTY_PRODUCT, productParams, productFilterLine,
  ExportButtons, printReport, runReport, money, today, fmtDate, esc,
} from './shared/ReportKit';

// Price List: item prices per price level (RETAIL, WHOLESALE …) as at a date
const PriceListReport = () => {
  const [levels, setLevels] = useState([]);
  const [priceLevel, setPriceLevel] = useState('');
  const [date, setDate] = useState(today());
  const [product, setProduct] = useState(EMPTY_PRODUCT);
  // off by default: with no price levels the report is the cost / selling price list of all items
  const [onlyPriced, setOnlyPriced] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => { inventoryApi.getPriceLevels().then(r => setLevels(r?.data || [])).catch(() => setLevels([])); }, []);

  const generate = () => runReport(() => inventoryApi.getPriceListReport({
    date, ...(priceLevel ? { priceLevel } : {}), ...productParams(product), ...(onlyPriced ? { onlyPriced: 'true' } : {}),
  }), setLoading, setData);
  const reset = () => { setPriceLevel(''); setDate(today()); setProduct(EMPTY_PRODUCT); setOnlyPriced(false); setData(null); };
  const lv = data?.levels || [];
  const filtersLine = [priceLevel && `Price level: ${priceLevel}`, productFilterLine(product)].filter(Boolean).join(' · ');
  const priceOf = (r, code) => r.prices[code]?.price;

  const exportExcel = async () => {
    const rows = [['PRICE LIST'], [`As at: ${fmtDate(data.date)}`], ...(filtersLine ? [[filtersLine]] : []), [],
      ['Item Code', 'Item Name', 'Group', 'Type', 'Unit', 'Cost', 'Selling Price', ...lv.map(l => `${l.code} — ${l.name}`)]];
    data.rows.forEach(r => rows.push([r.itemCode, r.itemName, r.groupName, r.typeName, r.unit, r.cost, r.sellingPrice, ...lv.map(l => priceOf(r, l.code) ?? '')]));
    await downloadXlsxFromAoa(rows, 'Price List', `price-list-${String(data.date).slice(0, 10)}.xlsx`, [{ wch: 18 }, { wch: 36 }, { wch: 18 }, { wch: 18 }, { wch: 8 }, { wch: 12 }, { wch: 14 }, ...lv.map(() => ({ wch: 16 }))],
      { numberFormat: '#,##0.00', numberColumns: [6, 7, ...lv.map((_, i) => 8 + i)] });
  };
  const print = () => printReport({
    title: 'Price List', subtitle: `As at ${fmtDate(data.date)}`, filtersLine, landscape: lv.length > 3,
    tableHtml: `<table><thead><tr><th>Item Code</th><th>Item Name</th><th>Unit</th><th class="r">Selling Price</th>${lv.map(l => `<th class="r">${esc(l.code)}</th>`).join('')}</tr></thead><tbody>${
      data.rows.map(r => `<tr><td>${esc(r.itemCode)}</td><td>${esc(r.itemName)}</td><td>${esc(r.unit)}</td><td class="r">${money(r.sellingPrice)}</td>${lv.map(l => `<td class="r">${priceOf(r, l.code) != null ? money(priceOf(r, l.code)) : '—'}</td>`).join('')}</tr>`).join('')
    }</tbody></table>`,
  });

  return (
    <ReportShell
      title="Price List" subtitle="Item cost and selling price, and the price on each price level" icon={TagIcon}
      loading={loading} onGenerate={generate} onReset={reset}
      filters={<>
        <ProductFilters value={product} onChange={setProduct} />
        {levels.length > 0 && <Field label="Price Level Code">
          <select value={priceLevel} onChange={(e) => setPriceLevel(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 min-w-[170px]">
            <option value="">All price levels</option>
            {levels.map(l => <option key={l._id} value={l.code}>{l.code} — {l.name}</option>)}
          </select>
        </Field>}
        <Field label="As at Date"><DateInput value={date} onChange={setDate} /></Field>
        {levels.length > 0 && <label className="flex items-center gap-2 h-[38px] text-sm text-gray-600 cursor-pointer select-none">
          <input type="checkbox" checked={onlyPriced} onChange={(e) => setOnlyPriced(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-indigo-600" />
          Only items with a level price
        </label>}
      </>}
      actions={data && <>
        <span className="text-sm text-gray-600">As at <b className="text-indigo-600">{fmtDate(data.date)}</b> · {data.rows.length} items · {lv.length} price level{lv.length === 1 ? '' : 's'}</span>
        <ExportButtons onExcel={exportExcel} onPrint={print} />
      </>}
    >
      {data && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Item Code</th>
                <th className="text-left px-4 py-2 font-medium">Item Name</th>
                <th className="text-left px-4 py-2 font-medium">Unit</th>
                <th className="text-right px-4 py-2 font-medium">Cost</th>
                <th className="text-right px-4 py-2 font-medium">Selling Price</th>
                {lv.map(l => <th key={l.code} className="text-right px-4 py-2 font-semibold text-indigo-700" title={l.name}>{l.code}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.rows.length === 0 && <tr><td colSpan={5 + lv.length} className="px-4 py-8 text-center text-gray-400 italic">{onlyPriced ? 'No item has a price on these levels yet' : 'No items match the filters'}</td></tr>}
              {data.rows.map(r => (
                <tr key={r.itemId} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-xs">{r.itemCode}</td>
                  <td className="px-4 py-2">{r.itemName}<div className="text-xs text-gray-400">{[r.groupName, r.typeName].filter(Boolean).join(' › ')}</div></td>
                  <td className="px-4 py-2 text-xs text-gray-500">{r.unit}</td>
                  <td className="px-4 py-2 text-right font-mono text-gray-500">{money(r.cost)}</td>
                  <td className="px-4 py-2 text-right font-mono">{money(r.sellingPrice)}</td>
                  {lv.map(l => {
                    const p = r.prices[l.code];
                    return <td key={l.code} className="px-4 py-2 text-right font-mono font-semibold" title={p?.effectiveFrom ? `From ${fmtDate(p.effectiveFrom)}` : ''}>{p ? money(p.price) : <span className="text-gray-300">—</span>}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ReportShell>
  );
};

export default PriceListReport;
